// Income.js — Mortgage Matrix (Refinance Flow)
// v18.0 — FINAL
// ✔ Employment Type removed
// ✔ Employment Status added with Option A list
// ✔ Self-Employed shows Business Name field
// ✔ Prefills from Mercury: employmentBasis, employmentCommenced, jobTitle
// ✔ Annual income required

import React, { useState, useEffect } from "react";
import CurrencyInput from "../ClientForm/CurrencyInput";
import styles from "./RefinanceForm.module.css";

function Income({ formData, setFormData, prevStep, nextStep }) {
  console.log("💾 Income.js received formData:", formData);

  const refinancingWithSomeone =
    !!formData.refinancingWithSomeone ||
    !!formData.applicant2 ||
    !!formData.coApplicant;

  const [activeApplicant, setActiveApplicant] = useState("applicant1");

  // ---------------------------------------------------
  // Prefill building from API data
  // ---------------------------------------------------
  const getApiPrefill = (appKey) => {
    const saved = formData.income?.[appKey];
    if (saved) return saved;

    // 1. Applicant 1 → formData.employmentIncomes
    if (appKey === "applicant1") {
      const api = formData.employmentIncomes?.[0];
      if (api) {
        return {
          employmentStatus: api.employmentBasis || "",
          employerName:
            api.employmentBasis === "Self-Employed"
              ? ""
              : api.employerName || "",
          businessName:
            api.employmentBasis === "Self-Employed"
              ? api.employerName || ""
              : "",
          description: api.jobTitle || "",
          startDate: api.employmentCommenced || "",
          incomeAmount: api.amount || "",
          otherIncomeType: "",
          otherIncomeAmount: "",
          otherIncomeFrequency: "",
        };
      }
    }

    // 2. Applicant 2 → formData.coApplicant.employmentIncomes
    if (appKey === "applicant2") {
      const api = formData.coApplicant?.employmentIncomes?.[0];
      if (api) {
        return {
          employmentStatus: api.employmentBasis || "",
          employerName:
            api.employmentBasis === "Self-Employed"
              ? ""
              : api.employerName || "",
          businessName:
            api.employmentBasis === "Self-Employed"
              ? api.employerName || ""
              : "",
          description: api.jobTitle || "",
          startDate: api.employmentCommenced || "",
          incomeAmount: api.amount || "",
          otherIncomeType: "",
          otherIncomeAmount: "",
          otherIncomeFrequency: "",
        };
      }
    }

    // fallback empty
    return {
      employmentStatus: "",
      employerName: "",
      businessName: "",
      description: "",
      startDate: "",
      incomeAmount: "",
      otherIncomeType: "",
      otherIncomeAmount: "",
      otherIncomeFrequency: "",
    };
  };

  const [incomeState, setIncomeState] = useState(
    getApiPrefill("applicant1")
  );
  const [submitted, setSubmitted] = useState(false);

  // Sync to parent
  useEffect(() => {
    setFormData((prev) => ({
      ...prev,
      income: {
        ...(prev.income || {}),
        [activeApplicant]: incomeState,
      },
    }));
  }, [incomeState, activeApplicant]);

  // Switch applicants
  useEffect(() => {
    const src = getApiPrefill(activeApplicant);
    setIncomeState({ ...src });
    setSubmitted(false);
  }, [activeApplicant]);

  const updateField = (f, v) => {
    setIncomeState((prev) => ({ ...prev, [f]: v }));
  };

  // ---------------------------------------------------
  // Validation
  // ---------------------------------------------------
  const isEmploymentValid = Boolean(incomeState.employmentStatus);
  const isIncomeValid = Boolean(incomeState.incomeAmount);

  const handleNext = () => {
    setSubmitted(true);

    if (!isEmploymentValid) {
      alert("Please select employment status.");
      return;
    }

    if (!isIncomeValid) {
      alert("Please enter the annual income.");
      return;
    }

    nextStep();
  };

  const isSelfEmployed =
    incomeState.employmentStatus === "Self Employed";

  return (
    <div className={styles.container}>
      <div className={styles.stepHeaderCompact}>
        <div className={styles.stepBarTrack}>
          <div className={styles.stepBarFill} style={{ width: `71.4%` }} />
        </div>
        <div className={styles.stepMeta}>
          <span className={styles.stepCenter}>Step 5 of 7: Income</span>
        </div>
      </div>

      {/* Applicant Toggle */}
      {refinancingWithSomeone && (
        <div className={styles.buttonGroup} style={{ marginBottom: "20px" }}>
          <button
            type="button"
            className={
              activeApplicant === "applicant1" ? styles.activeBtn : ""
            }
            onClick={() => setActiveApplicant("applicant1")}
          >
            Applicant 1
          </button>

          <button
            type="button"
            className={
              activeApplicant === "applicant2" ? styles.activeBtn : ""
            }
            onClick={() => setActiveApplicant("applicant2")}
          >
            Applicant 2
          </button>
        </div>
      )}

      <h3>
        Income Details —{" "}
        {activeApplicant === "applicant1"
          ? "Applicant 1"
          : "Applicant 2"}
      </h3>

      <p>Tell us about your income for this application.</p>

      {/* EMPLOYMENT STATUS */}
      <div className={styles.inputGroup}>
        <label>
          Employment Status <span className={styles.required}>*</span>
        </label>
        <select
          value={incomeState.employmentStatus}
          onChange={(e) =>
            updateField("employmentStatus", e.target.value)
          }
        >
          <option value="">Select Employment Status</option>
          <option value="Full Time">Full Time</option>
          <option value="Part Time">Part Time</option>
          <option value="Casual">Casual</option>
          <option value="Contract">Contract</option>
          <option value="Self Employed">Self Employed</option>
        </select>
      </div>

      {/* EMPLOYER NAME */}
      {!isSelfEmployed && (
        <div className={styles.inputGroup}>
          <label>Employer Name</label>
          <input
            type="text"
            value={incomeState.employerName}
            onChange={(e) =>
              updateField("employerName", e.target.value)
            }
          />
        </div>
      )}

      {/* BUSINESS NAME */}
      {isSelfEmployed && (
        <div className={styles.inputGroup}>
          <label>Business Name</label>
          <input
            type="text"
            value={incomeState.businessName}
            onChange={(e) =>
              updateField("businessName", e.target.value)
            }
          />
        </div>
      )}

      {/* DESCRIPTION */}
      <div className={styles.inputGroup}>
        <label>Role / Description</label>
        <input
          type="text"
          value={incomeState.description}
          onChange={(e) =>
            updateField("description", e.target.value)
          }
        />
      </div>

      {/* START DATE */}
      <div className={styles.inputGroup}>
        <label>Start Date</label>
        <input
          type="date"
          value={incomeState.startDate}
          onChange={(e) =>
            updateField("startDate", e.target.value)
          }
        />
      </div>

      {/* INCOME */}
      <div className={styles.inputGroup}>
        <label>
          Annual Income <span className={styles.required}>*</span>
        </label>
        <CurrencyInput
          value={incomeState.incomeAmount}
          onChange={(val) => updateField("incomeAmount", val)}
          placeholder="$0"
        />
      </div>

      {/* OTHER INCOME */}
      <hr style={{ margin: "2rem 0 1.5rem" }} />

      <h4>Other Income (optional)</h4>

      <div className={styles.inputGroup}>
        <label>Type of Other Income</label>
        <select
          value={incomeState.otherIncomeType}
          onChange={(e) =>
            updateField("otherIncomeType", e.target.value)
          }
        >
          <option value="">Select Type</option>
          <option value="Dividends">Dividends</option>
          <option value="Family Allowance">Family Allowance</option>
          <option value="Maintenance">Maintenance</option>
          <option value="Other">Other</option>
        </select>
      </div>

      <div className={styles.inputGroup}>
        <label>Other Income Amount</label>
        <CurrencyInput
          value={incomeState.otherIncomeAmount}
          onChange={(val) =>
            updateField("otherIncomeAmount", val)
          }
        />
      </div>

      <div className={styles.inputGroup}>
        <label>Other Income Frequency</label>
        <select
          value={incomeState.otherIncomeFrequency}
          onChange={(e) =>
            updateField("otherIncomeFrequency", e.target.value)
          }
        >
          <option value="">Select Frequency</option>
          <option value="Weekly">Weekly</option>
          <option value="Fortnightly">Fortnightly</option>
          <option value="Monthly">Monthly</option>
          <option value="Annually">Annually</option>
        </select>
      </div>

      {/* NAVIGATION */}
      <div className={styles.navBtns}>
        <button type="button" onClick={prevStep}>
          ← Back
        </button>

        <button
          type="button"
          className={styles.nextBtn}
          disabled={!isEmploymentValid || !isIncomeValid}
          onClick={handleNext}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

export default Income;
